import React, { useState, useRef, useEffect} from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import {generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import { awardToken } from "../../awardtoken.js";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg"
const PASS_THRESHOLD = 80;       // % required to unlock next chapter


export const Grade11BiologyLessonPages = [
    /* ───────────────────────── 0. INTRODUCTION ───────────────────────── */
    {
      title: "Introduction to Grade 11 Biology",
      topic: "Course Introduction",
      content: (
        <>
          <p>
            Welcome to Grade 11 Biology! Over the next eight units we’ll explore
            life from molecules to the biosphere—plus 🚀 glimpses of where the
            field is heading by 2035.
          </p>
          <ul>
            <li>Cells & Cellular Processes</li>
            <li>Genetics & Inheritance</li>
            <li>Evolution & Biodiversity</li>
            <li>Human Body Systems</li>
            <li>Plant Biology</li>
            <li>Ecology & the Environment</li>
          </ul>
        </>
      ),
    },
  
    /* ╔═══════════════════ CHAPTER 1 – CELLS ═══════════════════╗ */
{
    title: "1.1 Cell Theory & Cell Types",
    topic: "Cells & Cellular Processes",
    content: (
      <>
        <h4>Key Ideas & Historical Context</h4>
        <p>
          <strong>Cell Theory (1838–1858)</strong>—the combined work of
          Schleiden, Schwann & Virchow—declares that (1) all living organisms are
          made of cells, (2) the cell is the fundamental unit of structure and
          function, and (3) <em>omnis cellula e cellula</em>: every cell arises
          from a pre-existing cell.
        </p>
  
        <h5>Modern Extension</h5>
        <ul>
          <li>Energy flow (metabolism) occurs within cells.</li>
          <li>Cells contain hereditary information (DNA/RNA) passed to offspring.</li>
          <li>
            Today, 🚀 <em>single-cell omics</em> can sequence RNA & chromatin in
            individual cells, revealing hidden states like “cancer stem-like
            cells.”
          </li>
        </ul>
  
        <h5>Cell Types in a Nutshell</h5>
        <table>
          <thead>
            <tr><th style={{textAlign:"left"}}>Feature</th><th>Prokaryote</th><th>Eukaryote</th></tr>
          </thead>
          <tbody>
            <tr><td>Nuclear membrane</td><td>❌</td><td>✅</td></tr>
            <tr><td>Typical diameter</td><td>~1 µm</td><td>10–100 µm</td></tr>
            <tr><td>Division</td><td>Binary fission</td><td>Mitosis / meiosis</td></tr>
          </tbody>
        </table>
  
        <p style={{marginTop:12}}>
          <strong>Analogy:</strong> Think of a prokaryote as a cozy studio
          apartment, while a eukaryote is a multi-room house with dedicated
          kitchens (mitochondria) and libraries (nucleus).
        </p>
  
        <p><b>Quick Check 🔎:</b> Sketch a bacterial cell and label <em>nucleoid,
        ribosome, plasma membrane</em>. What structure is missing compared with a
        human cheek cell?</p>
      </>
    ),
  },
  {
    title: "1.2 Organelles & Internal Compartments",
    topic: "Cells & Cellular Processes",
    content: (
      <>
        <h4>The Organelle Tour</h4>
        <p>
          Each organelle is like a department in a high-tech factory. Matching
          function to analogy helps memory:
        </p>
        <ul>
          <li><b>Nucleus 📁:</b> CEO’s office—stores blueprints (DNA).</li>
          <li><b>Rough ER 🛠️:</b> Assembly line—ribosomes bolt amino-acids into proteins.</li>
          <li><b>Golgi 📦:</b> Shipping &amp; receiving—tags, folds & exports cargo.</li>
          <li><b>Mitochondrion 🔋:</b> Powerplant—oxidizes fuel to ATP.</li>
          <li><b>Chloroplast 🌞:</b> Solar panel—captures photons, fixes carbon.</li>
          <li><b>Lysosome 🗑️:</b> Recycling center—acid hydrolases break waste into monomers.</li>
        </ul>
  
        <h5>Endosymbiont Theory 🔬</h5>
        <p>
          Mitochondria & chloroplasts have <em>double membranes</em> and
          <em>circular DNA</em>, evidence they were once free-living
          bacteria—an early example of biological merger & acquisition!
        </p>
  
        <p><em>Try this:</em> Visit <a href="https://blast.ncbi.nlm.nih.gov" target="_blank" rel="noreferrer">NCBI BLAST</a> and align a human
        mitochondrial gene (e.g., COX1) with its <i>E. coli</i> counterpart—note
        the % identity.</p>
      </>
    ),
  },
  {
    title: "1.3 Membranes & Transport",
    topic: "Cells & Cellular Processes",
    content: (
      <>
        <h4>Fluid-Mosaic Model 🧩</h4>
        <p>
          A cell membrane is a two-ply <em>phospholipid bilayer</em> embedded with
          proteins that drift like icebergs in a sea of lipids. Cholesterol acts
          as both antifreeze &amp; glue, maintaining fluidity.
        </p>
  
        <h5>Traffic Rules</h5>
        <ul>
          <li><b>Passive</b>: diffusion, osmosis, facilitated diffusion via channels.</li>
          <li><b>Active</b>: ATP-powered pumps (Na⁺/K⁺-ATPase), proton gradients.</li>
          <li><b>Bulk</b>: endocytosis (phagocytosis, pinocytosis), exocytosis.</li>
        </ul>
  
        <p>
          Medical link 💉: IV fluids must be <em>isotonic</em>; a hypotonic drip
          can lyse red blood cells (hemolysis).
        </p>
  
        <p>
          🚀 <strong>Future Focus:</strong> <em>DNA-origami nanopores</em> can be
          programmed to open in response to tumor acidity, releasing drugs exactly
          where needed.
        </p>
  
        <p><b>Self-Check:</b> Why can’t glucose simply diffuse across the membrane?
        What transporter solves this, and how is it regulated by insulin?</p>
      </>
    ),
  },
  {
    title: "1.4 Cellular Energy: Respiration & Photosynthesis",
    topic: "Cells & Cellular Processes",
    content: (
      <>
        <h4>Respiration 🔋</h4>
        <p>
          One glucose → up to <b>~30–32 ATP</b> (aerobic). Sequence:
          Glycolysis → Pyruvate oxidation → Krebs cycle → Electron-Transport
          Chain → ATP Synthase (chemiosmosis). Oxygen is the terminal
          electron acceptor.
        </p>
  
        <h4>Photosynthesis 🌞</h4>
        <p>
          Location = chloroplast. Light reactions in <em>thylakoid</em> membranes
          split water &amp; charge NADPH/ATP; the Calvin cycle in the stroma fixes
          CO₂ into G3P. Compare:
        </p>
        <ul>
          <li><b>C₃</b>: most plants, photorespiration risk.</li>
          <li><b>C₄</b>: maize; spatial separation, PEP carboxylase.</li>
          <li><b>CAM</b>: cacti; temporal separation (night vs. day).</li>
        </ul>
  
        <p>
          <em>Virtual Lab:</em> Launch the PhET “Cell Energy” simulator. Increase
          temperature by 10 °C—what happens to ATP output and why?
        </p>
  
        <p><b>Challenge Problem:</b> Calculate the P:O ratio (ATP/O₂) if the
        proton-motive force drops by 25 % — how might an uncoupling protein in
        brown fat alter this?</p>
      </>
    ),
  },
  
    /* ╔═══════════════════ CHAPTER 2 – GENETICS ══════════════════╗ */
{
    title: "2.1 Mendelian Genetics Refresher",
    topic: "Genetics & Inheritance",
    content: (
      <>
        <h4>Key Principles</h4>
        <ul>
          <li>
            <b>Law of Segregation:</b> Each gamete carries only one allele for each gene due to separation during meiosis.
          </li>
          <li>
            <b>Law of Independent Assortment:</b> Genes for different traits can segregate independently during gamete formation (except for linked genes).
          </li>
          <li>
            <b>Monohybrid vs. Dihybrid Crosses:</b> Monohybrid (one trait, 3:1 ratio); Dihybrid (two traits, 9:3:3:1 ratio). Apply test cross logic to determine unknown genotypes.
          </li>
          <li>
            <b>Modern Outlook:</b> Next-gen sequencing confirms Mendel’s laws, but also reveals complex exceptions (gene linkage, epigenetics).
          </li>
        </ul>
        <p>
          <b>Practice:</b> Drag-and-drop the correct genotypes and phenotypes into a Punnett square (AA × Aa). Predict probabilities for future generations!
        </p>
        <p>
          <i>Looking ahead: AI tools are accelerating the discovery of non-Mendelian inheritance patterns, shaping the future of genetic counseling.</i>
        </p>
      </>
    ),
  },
  {
    title: "2.2 Beyond Mendel: Extensions & Exceptions",
    topic: "Genetics & Inheritance",
    content: (
      <>
        <h4>Genetic Complexity</h4>
        <ul>
          <li>
            <b>Incomplete Dominance:</b> Heterozygotes show a blend (e.g., red + white flowers = pink).
          </li>
          <li>
            <b>Codominance:</b> Both alleles are expressed equally (e.g., ABO blood groups: IA and IB).
          </li>
          <li>
            <b>Epistasis:</b> One gene masks or modifies the effect of another (e.g., coat color in Labrador retrievers).
          </li>
          <li>
            <b>Polygenic Traits:</b> Multiple genes contribute to quantitative traits (e.g., height, skin color). Continuous variation, not just “either/or.”
          </li>
          <li>
            <b>Future Impact:</b> Polygenic risk scores are enabling early prediction of disease risk, revolutionizing precision medicine and personalized healthcare.
          </li>
        </ul>
        <p>
          <b>Case Study:</b> Calculate the probability of a child inheriting AB blood type from parents with genotypes IAi and IBi.
        </p>
        <p>
          <i>Frontiers: Epigenetics and environmental influence on gene expression—how lifestyle could impact future generations.</i>
        </p>
      </>
    ),
  },
  {
    title: "2.3 Molecular Genetics: DNA to Protein",
    topic: "Genetics & Inheritance",
    content: (
      <>
        <h4>From Blueprint to Function</h4>
        <ul>
          <li>
            <b>DNA Structure:</b> Double-helix, antiparallel strands, complementary base-pairing. Watson & Crick’s model, Rosalind Franklin’s X-ray diffraction.
          </li>
          <li>
            <b>DNA Replication:</b> Semi-conservative; each strand serves as template. Enzymes: helicase (unwinds), DNA polymerase (synthesizes), ligase (joins fragments). Distinction between leading/lagging strands.
          </li>
          <li>
            <b>Central Dogma:</b> Information flow—DNA <span style={{fontWeight: "bold"}}>→</span> RNA (transcription: initiation, elongation, termination) <span style={{fontWeight: "bold"}}>→</span> Protein (translation: ribosome assembly, polypeptide synthesis, stop codon).
          </li>
          <li>
            <b>Applied Genetics:</b> mRNA vaccines (COVID-19), gene therapy, and synthetic biology—all rely on our understanding of this process.
          </li>
        </ul>
        <p>
          <b>Interactive:</b> Simulate transcription & translation: drag nucleotides to build mRNA, then match codons to amino acids.
        </p>
        <p>
          <i>Next-gen: CRISPR and RNA-editing tools are poised to treat inherited diseases at the source.</i>
        </p>
      </>
    ),
  },
  {
    title: "2.4 Biotechnology & CRISPR Ethics",
    topic: "Genetics & Inheritance",
    content: (
      <>
        <h4>Genomics Revolution</h4>
        <ul>
          <li>
            <b>PCR:</b> Amplifies DNA—cornerstone of genetic testing, forensics, and diagnostics.
          </li>
          <li>
            <b>Sanger vs. Next-Gen Sequencing:</b> Sanger is precise for small regions; next-gen enables rapid whole-genome sequencing.
          </li>
          <li>
            <b>CRISPR-Cas9:</b> “Molecular scissors” for precise gene editing. Expanding into agriculture, medicine, and disease research.
          </li>
          <li>
            <b>Gene Drives:</b> Can bias inheritance to spread traits rapidly through populations—potential for eradicating disease vectors (e.g., malaria mosquitoes).
          </li>
          <li>
            <b>Bioethics:</b> Debates on gene editing—potential benefits vs. unintended consequences, equity, and consent. International frameworks (WHO, UNESCO) guide responsible innovation.
          </li>
        </ul>
        <p>
          <b>Debate Prompt:</b> Should germline editing ever be permitted? Post a 200-word argument on the discussion board and review at least one peer response.
        </p>
        <p>
          <i>Future scope: How will society navigate gene editing’s promise—and its risks? Could CRISPR democratize healthcare, or deepen global inequity?</i>
        </p>
      </>
    ),
  },
  
  
    /* ╔═══════════ CHAPTER 3 – EVOLUTION & BIODIVERSITY ═══════════╗ */
{
    title: "3.1 Mechanisms of Evolution",
    topic: "Evolution & Biodiversity",
    content: (
      <>
        <h4>Core Forces Shaping Life</h4>
        <ul>
          <li>
            <b>Mutation:</b> Source of genetic diversity—random changes in DNA that can create new alleles and drive adaptation.
          </li>
          <li>
            <b>Natural Selection:</b> Non-random process—individuals with advantageous traits survive and reproduce, shifting allele frequencies.
          </li>
          <li>
            <b>Genetic Drift:</b> Random fluctuations in allele frequencies, especially significant in small populations (e.g., bottleneck, founder effect).
          </li>
          <li>
            <b>Gene Flow:</b> Movement of genes between populations via migration, introducing new genetic material and increasing variation.
          </li>
          <li>
            <b>Hardy–Weinberg Equilibrium:</b> Predicts allele and genotype frequencies in ideal populations; used as a baseline to detect evolution.
            <ul>
              <li>
                <b>Practice:</b> Given real-world allele frequencies, use the Hardy–Weinberg formula to model expected vs. observed results.
              </li>
            </ul>
          </li>
          <li>
            <b>Next Frontiers:</b> Ancient-DNA (aDNA) analysis is unveiling unexpected patterns in human and animal migration, extinction, and adaptation.
          </li>
        </ul>
        <p>
          🚀 <i>Case Example:</i> Sequencing Neanderthal genomes is challenging long-held ideas about human evolution, interbreeding, and disease resistance.
        </p>
      </>
    ),
  },
  {
    title: "3.2 Speciation & Adaptive Radiation",
    topic: "Evolution & Biodiversity",
    content: (
      <>
        <h4>How New Species Arise</h4>
        <ul>
          <li>
            <b>Allopatric Speciation:</b> Geographic isolation splits populations, leading to divergent evolution (e.g., islands, mountains).
          </li>
          <li>
            <b>Sympatric Speciation:</b> New species arise in the same location, often due to genetic changes (e.g., polyploidy in plants) or niche differentiation.
          </li>
          <li>
            <b>Reproductive Isolation:</b> Barriers (prezygotic and postzygotic) prevent gene flow—critical for maintaining distinct species.
          </li>
          <li>
            <b>Adaptive Radiation:</b> Rapid diversification when organisms colonize new environments or after mass extinctions. 
            <ul>
              <li>
                <b>Case Study:</b> Darwin’s finches—multiple species evolved from a single ancestor, each adapted to a different ecological niche.
              </li>
            </ul>
          </li>
          <li>
            <b>Future Insights:</b> Genomics is revealing “speciation genes” and how environmental pressures (including climate change) may drive future radiations.
          </li>
        </ul>
        <p>
          <b>Interactive:</b> Simulate speciation in a virtual ecosystem: alter variables (isolation, mutation rate, selection) and observe evolutionary outcomes.
        </p>
        <p>
          <i>Frontier Thought:</i> Could gene editing accelerate or reverse speciation? What are the ecological risks and responsibilities?
        </p>
      </>
    ),
  },
  {
    title: "3.3 Classification & Phylogeny",
    topic: "Evolution & Biodiversity",
    content: (
      <>
        <h4>Mapping Life’s Relationships</h4>
        <ul>
          <li>
            <b>Three-Domain System:</b> Bacteria, Archaea, Eukarya—reflecting molecular (rRNA) evidence of evolutionary history.
          </li>
          <li>
            <b>Kingdom Overview:</b> Organization of life into increasingly specific taxonomic ranks—domain, kingdom, phylum, etc.
          </li>
          <li>
            <b>Cladograms:</b> Diagrams that visualize evolutionary relationships based on shared characteristics and DNA data.
          </li>
          <li>
            <b>Molecular Clocks:</b> Using mutation rates to estimate divergence times—reshaping our timelines for evolutionary events.
          </li>
          <li>
            <b>DNA Barcoding:</b> Short genetic sequences (like COI gene) identify species—empowering conservation and ecosystem monitoring.
          </li>
          <li>
            <b>AI-Assisted Taxonomy:</b> Machine learning is accelerating species discovery, automating morphological and genetic analysis, and predicting evolutionary links.
          </li>
        </ul>
        <p>
          <b>Inquiry:</b> Build and interpret a simple cladogram with real DNA sequence data; use an online barcoding tool to identify “mystery species.”
        </p>
        <p>
          🚀 <i>Looking Ahead:</i> As biodiversity loss accelerates, digital phylogenies and AI tools may be our best hope for understanding—and protecting—Earth’s tree of life.
        </p>
      </>
    ),
  },
  
  
    /* ╔══════════════════ CHAPTER 4 – HUMAN SYSTEMS ═══════════════════╗ */
{
    title: "4.1 Digestive & Circulatory Systems",
    topic: "Human Body Systems",
    content: (
      <>
        <h4>Integrating Energy & Transport</h4>
        <ul>
          <li>
            <b>Digestive Enzymes & Nutrient Absorption:</b> Breakdown of macromolecules (carbs, proteins, lipids) in the mouth, stomach, and intestines. Microvilli maximize absorption in the small intestine.
          </li>
          <li>
            <b>Gut Microbiome:</b> Trillions of symbiotic microbes influence digestion, immunity, and even mental health. Cutting-edge research explores personalized “microbiome medicine.”
          </li>
          <li>
            <b>Heart Anatomy & Double Circulation:</b> Four-chambered heart separates oxygen-rich and oxygen-poor blood, powering systemic and pulmonary circuits.
          </li>
          <li>
            <b>Blood Composition:</b> Red cells (O₂ transport), white cells (immunity), platelets (clotting), plasma (nutrient, hormone, waste transport).
          </li>
          <li>
            <b>Real-world Relevance:</b> Cardiovascular disease is the #1 global killer; advances in stem cell therapy and 3D-printed tissues aim to revolutionize treatment.
          </li>
        </ul>
        <p>
          <b>Case Study:</b> Trace a molecule of glucose from digestion to absorption, through the bloodstream, and to its use in cellular respiration.
        </p>
        <p>
          <i>Frontier Insight:</i> AI is helping decode the “gut-heart axis,” predicting disease risk and informing next-generation therapies.
        </p>
      </>
    ),
  },
  {
    title: "4.2 Respiratory & Excretory Systems",
    topic: "Human Body Systems",
    content: (
      <>
        <h4>Exchange & Elimination</h4>
        <ul>
          <li>
            <b>Gas Exchange Surfaces:</b> Alveoli in lungs maximize surface area for rapid O₂/CO₂ diffusion. Smoking/vaping and air pollution threaten this critical interface.
          </li>
          <li>
            <b>Hemoglobin Affinity:</b> Binds O₂ in lungs, releases it in tissues; curve shifts in response to pH, CO₂, temperature (“Bohr effect”). 
          </li>
          <li>
            <b>Nephron Structure:</b> Functional unit of the kidney—filters blood, reabsorbs water/nutrients, secretes waste (urea).
          </li>
          <li>
            <b>Osmoregulation:</b> Kidneys precisely balance body fluids and electrolytes—key for blood pressure and health.
          </li>
          <li>
            <b>Emerging Tech:</b> Wearable sensors for monitoring lung/kidney function, and bioengineered organs for transplantation.
          </li>
        </ul>
        <p>
          <b>Practice:</b> Model how dehydration or high altitude impacts gas exchange and urine concentration.
        </p>
        <p>
          <i>Futurist Lens:</i> Could gene editing someday enhance human oxygen efficiency or reduce kidney disease globally?
        </p>
      </>
    ),
  },
  {
    title: "4.3 Nervous & Endocrine Systems",
    topic: "Human Body Systems",
    content: (
      <>
        <h4>Communication & Control</h4>
        <ul>
          <li>
            <b>Neuron Structure:</b> Dendrites (input), cell body (integration), axon (output); myelin sheath speeds signals.
          </li>
          <li>
            <b>Action Potentials & Synaptic Transmission:</b> Electrical impulses trigger neurotransmitter release; basis for thought, movement, sensation.
          </li>
          <li>
            <b>Endocrine Glands:</b> Hypothalamus, pituitary, thyroid, adrenals, pancreas, gonads—release hormones into blood.
          </li>
          <li>
            <b>Hormone Feedback Loops:</b> Negative feedback keeps body systems in balance (e.g., blood glucose regulation by insulin/glucagon).
          </li>
          <li>
            <b>Frontiers:</b> Brain-computer interfaces, neural stem cells, and precision hormone therapies are redefining how we treat neurological and metabolic disease.
          </li>
        </ul>
        <p>
          <b>Interactive:</b> Simulate a reflex arc or hormonal response to a stressor in an online activity.
        </p>
        <p>
          <i>Next Step:</i> Will neuroprosthetics and personalized endocrinology enable superhuman abilities or change our sense of self?
        </p>
      </>
    ),
  },
  {
    title: "4.4 Immune System & Vaccinology",
    topic: "Human Body Systems",
    content: (
      <>
        <h4>Defense & Innovation</h4>
        <ul>
          <li>
            <b>Innate Immunity:</b> Immediate, non-specific defenses (skin, inflammation, phagocytes).
          </li>
          <li>
            <b>Adaptive Immunity:</b> Slower, highly specific—B and T lymphocytes, memory cells for long-term protection.
          </li>
          <li>
            <b>Antibody Classes:</b> IgG, IgM, IgA, IgE, IgD—each with unique roles in defense and diagnosis.
          </li>
          <li>
            <b>Vaccines:</b> Traditional (inactivated, live-attenuated) and next-gen (mRNA, viral vector) platforms.
          </li>
          <li>
            <b>Biotech in Action:</b> mRNA vaccines (COVID-19) have accelerated timelines from years to months; new platforms target cancer and chronic diseases.
          </li>
        </ul>
        <p>
          <b>Debate Prompt:</b> Should mRNA technology be prioritized for future pandemics? Defend your position and consider global access.
        </p>
        <p>
          🚀 <i>Vision:</i> The immune system is being “hacked” for therapies beyond infection—think autoimmune control, allergy prevention, and anti-cancer immunity.
        </p>
      </>
    ),
  },

    /* ╔══════════════════ CHAPTER 5 – PLANT BIOLOGY ══════════════════╗ */
{
  title: "5.1 Plant Anatomy: Tissues & Organs",
  topic: "Plant Biology",
  content: (
    <>
      <h4>Building Blocks of Plant Life</h4>
      <ul>
        <li>
          <b>Dermal Tissue:</b> Outermost layer (epidermis), protects against water loss, pathogens; may form cuticle or trichomes.
        </li>
        <li>
          <b>Ground Tissue:</b> Majority of plant mass—photosynthesis (parenchyma), storage (parenchyma), support (collenchyma, sclerenchyma).
        </li>
        <li>
          <b>Vascular Tissue:</b> Xylem (water/mineral transport) & phloem (sugar/organic compound transport); crucial for plant height and resource distribution.
        </li>
        <li>
          <b>Organs:</b> Roots (anchorage, absorption), stems (support, transport), leaves (photosynthesis, gas exchange).
        </li>
        <li>
          <b>Emerging Science:</b> Study of plant “root-brain” networks—how plants sense and respond to their environment in surprisingly complex ways.
        </li>
      </ul>
      <p>
        <b>Case Study:</b> Compare the specialized adaptations of root hairs, cactus stems, or water lily leaves.
      </p>
      <p>
        <i>Futurist Note:</i> Biodesigners are now using plant tissues for sustainable materials and “living architecture.”
      </p>
    </>
  ),
},
{
  title: "5.2 Transport & Transpiration",
  topic: "Plant Biology",
  content: (
    <>
      <h4>Moving Water, Minerals, and Food</h4>
      <ul>
        <li>
          <b>Cohesion–Tension Mechanism:</b> Explains how water moves upward from roots to leaves against gravity, driven by transpiration and the hydrogen bonding of water.
        </li>
        <li>
          <b>Pressure–Flow Hypothesis:</b> Describes how sugars flow from source (leaves) to sink (roots, fruits) in phloem—requiring osmotic pressure.
        </li>
        <li>
          <b>Stomata:</b> Tiny pores on leaves regulate gas exchange and water loss; critical to plant survival and global climate regulation.
        </li>
        <li>
          <b>Hands-On:</b> Potometer lab—measure transpiration under varying light, humidity, or wind. Digital sensors now allow real-time analysis.
        </li>
        <li>
          <b>Future Perspective:</b> Genetically modified crops with drought-resistant traits are already helping to secure food supply in changing climates.
        </li>
      </ul>
      <p>
        <b>Challenge:</b> Predict how rising CO₂ or shifting rainfall patterns might alter transpiration rates worldwide.
      </p>
      <p>
        🚀 <i>Next-Gen:</i> Smart agriculture uses sensors and AI to monitor plant water use and optimize irrigation, reducing waste and boosting yield.
      </p>
    </>
  ),
},
{
  title: "5.3 Hormones & Tropisms",
  topic: "Plant Biology",
  content: (
    <>
      <h4>Signals & Responses</h4>
      <ul>
        <li>
          <b>Auxin:</b> Promotes stem elongation, root growth, and directs phototropism (growth toward light).
        </li>
        <li>
          <b>Gibberellin:</b> Stimulates growth and seed germination—used in agriculture to increase fruit size and yield.
        </li>
        <li>
          <b>Ethylene:</b> Gas hormone; triggers fruit ripening, leaf drop, and response to stress.
        </li>
        <li>
          <b>Tropisms:</b> 
          <ul>
            <li><b>Phototropism:</b> Growth towards light.</li>
            <li><b>Gravitropism:</b> Growth in response to gravity (roots down, shoots up).</li>
            <li><b>Thigmotropism:</b> Growth response to touch (e.g., tendrils coiling).</li>
          </ul>
        </li>
        <li>
          <b>Frontiers:</b> CRISPR-edited crops—designed for resistance to pests, drought, and extreme conditions—are redefining the future of global food security.
        </li>
      </ul>
      <p>
        <b>Experiment:</b> Design a simple experiment to observe phototropism in bean seedlings or rapid ethylene-triggered ripening in fruit.
      </p>
      <p>
        🚀 <i>Future Insight:</i> Scientists are exploring plant “communication”—using electrical signals and chemical cues to enhance growth or resilience, possibly even controlling plant responses via bioengineering.
      </p>
    </>
  ),
},
  
   /* ╔═════════════════ CHAPTER 6 – ECOLOGY & ENVIRONMENT ═════════════╗ */
{
    title: "6.1 Population Dynamics",
    topic: "Ecology & Environment",
    content: (
      <>
        <h4>How Populations Grow & Adapt</h4>
        <ul>
          <li>
            <b>Exponential Growth:</b> Rapid, unchecked increase when resources are abundant; unsustainable in real ecosystems.
          </li>
          <li>
            <b>Logistic Growth:</b> Growth slows as population nears carrying capacity (K); produces an “S-curve.”
          </li>
          <li>
            <b>Carrying Capacity (K):</b> Maximum population an environment can sustainably support, shaped by limiting factors like food, space, and disease.
          </li>
          <li>
            <b>r/K Selection Theory:</b> <b>r-strategists</b> (e.g., bacteria, insects): many offspring, low investment, thrive in unpredictable environments. <b>K-strategists</b> (e.g., elephants, humans): few offspring, high parental care, stable environments.
          </li>
          <li>
            <b>Modern Lens:</b> Conservation biologists use population models to forecast species declines and guide recovery plans.
          </li>
        </ul>
        <p>
          <b>Interactive:</b> Use a population simulator: change birth/death rates or resource limits and watch population curves evolve.
        </p>
        <p>
          <i>Futurist Note:</i> Could gene drives or rewilding strategies reshape population growth and extinction risk?
        </p>
      </>
    ),
  },
  {
    title: "6.2 Community Interactions",
    topic: "Ecology & Environment",
    content: (
      <>
        <h4>The Web of Life</h4>
        <ul>
          <li>
            <b>Predation & Competition:</b> Predators control prey populations; competition for limited resources can lead to exclusion or coexistence.
          </li>
          <li>
            <b>Mutualism & Symbiosis:</b> Two species benefit from one another (e.g., pollinators & flowers, mycorrhizal fungi & plant roots).
          </li>
          <li>
            <b>Keystone Species:</b> Disproportionately large impact on community structure (e.g., sea otters, wolves in Yellowstone).
          </li>
          <li>
            <b>Trophic Cascades:</b> Ripple effects when top predators are added or removed—alters entire food webs.
          </li>
          <li>
            <b>Emerging Science:</b> DNA metabarcoding and remote sensing are uncovering hidden interactions in complex communities.
          </li>
        </ul>
        <p>
          <b>Case Study:</b> Analyze how the reintroduction of wolves changed Yellowstone’s ecosystem—apply trophic cascade thinking.
        </p>
        <p>
          <i>Frontier Insight:</i> How can understanding community networks guide ecosystem restoration in the Anthropocene?
        </p>
      </>
    ),
  },
  {
    title: "6.3 Biogeochemical Cycles",
    topic: "Ecology & Environment",
    content: (
      <>
        <h4>Nature’s Recycling Systems</h4>
        <ul>
          <li>
            <b>Water Cycle:</b> Evaporation, condensation, precipitation, runoff—essential for all living things.
          </li>
          <li>
            <b>Carbon Cycle:</b> Carbon moves among atmosphere, biosphere, oceans, and rocks. Photosynthesis stores carbon; respiration and combustion release it.
          </li>
          <li>
            <b>Nitrogen Cycle:</b> Nitrogen fixation by bacteria makes atmospheric N₂ available to plants; decomposition and denitrification return it to the air.
          </li>
          <li>
            <b>Human Disruptions:</b> Fossil fuel burning, deforestation, fertilizer runoff—contribute to climate change, ocean acidification, and dead zones.
          </li>
          <li>
            <b>Innovation:</b> Bioremediation, green chemistry, and synthetic biology aim to restore balance and reduce pollution.
          </li>
        </ul>
        <p>
          <b>Practice:</b> Map how a single carbon atom moves through the biosphere, hydrosphere, and atmosphere—where do humans alter its journey?
        </p>
        <p>
          <i>Looking Ahead:</i> Could future biotech engineer microbes to capture CO₂ or recycle nitrogen more efficiently?
        </p>
      </>
    ),
  },
  {
    title: "6.4 Climate Change Biology & Resilience",
    topic: "Ecology & Environment",
    content: (
      <>
        <h4>Adapting to a Changing Planet</h4>
        <ul>
          <li>
            <b>Anthropogenic Warming:</b> Greenhouse gas emissions are causing rapid global temperature rise—confirmed by satellite, ice core, and ocean data.
          </li>
          <li>
            <b>Ecological Impacts:</b> Habitat loss, species extinction, shifting ranges, coral bleaching, ocean acidification, altered migration and phenology.
          </li>
          <li>
            <b>Resilience & Adaptation:</b> Conservation corridors, climate-resilient crops, urban greening, and managed retreat strategies.
          </li>
          <li>
            <b>Rewilding:</b> Large-scale restoration of natural processes and keystone species (e.g., bison, beavers, wolves) to rebuild ecosystems.
          </li>
          <li>
            <b>Carbon-Negative Tech:</b> Direct air capture, biochar, enhanced weathering—novel tools to pull carbon out of the atmosphere.
          </li>
        </ul>
        <p>
          <b>Debate Prompt:</b> Should society focus more on climate adaptation or mitigation? Develop a position and propose an innovative solution.
        </p>
        <p>
          🚀 <i>Next Horizon:</i> AI models are predicting climate “tipping points”—can technology and ecosystem wisdom together avert the worst outcomes?
        </p>
      </>
    ),
  },
  
  
    /* ╔══════════════ CHAPTER 7 – CAPSTONE & REVIEW ════════════════╗ */
{
    title: "Course Review & 2035 Biology Project",
    topic: "Capstone",
    isFinalReview: true,
    content: (
      <>
        <h4>📊 50-Question Final Review Quiz</h4>
        <ul>
          <li>
            Comprehensive, adaptive quiz covering key concepts, processes, and applications from all units.
          </li>
          <li>
            Instant feedback and personalized study tips for missed questions.
          </li>
          <li>
            <b>Interactive:</b> Collaborative quiz battles—form teams and compete for high scores!
          </li>
        </ul>
        <h4>🚀 “Biology in 2035” Vision Deck</h4>
        <ul>
          <li>
            <b>Challenge:</b> Imagine the world in 2035. How will advances in at least two biology units (e.g., genetics & climate biology, plant science & medicine) transform daily life, society, or the planet?
          </li>
          <li>
            Build a mini pitch deck (slides or digital poster) connecting current biology to future breakthroughs—use real trends (CRISPR, climate tech, AI-driven health, etc.) to justify your predictions.
          </li>
          <li>
            <b>Bonus:</b> Interview a scientist, entrepreneur, or community leader about their vision for the future of biology.
          </li>
          <li>
            Present your project to classmates or upload to the “2035 Gallery”—vote for the most inspiring ideas.
          </li>
        </ul>
        <p>
          <i>Capstone Reflection:</i> How will biology empower you, your community, or the world to solve challenges and create opportunity by 2035? What is your role in shaping that future?
        </p>
      </>
    ),
  },
  
  ];
  

  export default function Grade11BiologyLesson({ onAwardTokens }) {
    /* ─────────── basic UI state ─────────── */
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizResult, setQuizResult] = useState(null);   // {score,total}
    const hasAwardedToken = useRef(false);
  
    /* ─────────── user & progress persistence ─────────── */
    const [user, setUser] = useState(null);
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("bio11ChaptersPassed") || "{}")
    );
  
    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);
  
    /* ─────────── prepare quiz for current topic ─────────── */
    const currentTopic = Grade11BiologyLessonPages[current]?.topic;
    const quizQuestions = currentTopic
      ? generateAdvancedScienceQuiz({
          subject: "Biology",
          grade: 11,
          topic: currentTopic,
          count: 5,
        })
      : [];
  
    /* ─────────── quiz completion handler ─────────── */
const handleQuizFinish = (score, total) => {
  setShowQuiz(false);
  setQuizResult({ score, total });
  hasAwardedToken.current = false; // reset for next quiz

  const percent = total > 0 ? Math.round((score / total) * 100) : 0;
  if (percent >= PASS_THRESHOLD) {
    // mark chapter as passed
    setChaptersPassed(prev => {
      const updated = { ...prev, [currentTopic]: true };
      localStorage.setItem("bio11ChaptersPassed", JSON.stringify(updated));
      return updated;
    });

    // award token
    if (!hasAwardedToken.current) {
      if (typeof onAwardTokens === "function") onAwardTokens(1);
      else awardToken();
      hasAwardedToken.current = true;
    }

    // --- Save quiz result for Student Dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults[`biology_g11_${currentTopic}`] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // ---------------------------------------------
  }
};
  
    /* ─────────── gated navigation logic ─────────── */
    const handleNextPage = () => {
      const nextIndex = current + 1;
      const nextPage = Grade11BiologyLessonPages[nextIndex];
  
      // Are we crossing into a new chapter?
      if (
        nextPage &&
        nextPage.topic !== currentTopic &&
        currentTopic !== "Course Introduction" &&          // intro has no gate
        !chaptersPassed[currentTopic]
      ) {
        // force the quiz instead of advancing
        setShowQuiz(true);
        setQuizResult(null);
        return;
      }
  
      setCurrent(nextIndex);
    };
  
    /* ─────────── render: quiz mode ─────────── */
    if (showQuiz && quizQuestions.length) {
      return (
        <ScienceStoryModeQuiz
          questions={quizQuestions}
          studentAvatar={user?.avatar ?? defaultProfile}
          onFinish={handleQuizFinish}
        />
      );
    }
  
    /* ─────────── render: quiz result splash ─────────── */
    if (quizResult) {
        const { score, total } = quizResult;
        const percent = total > 0 ? Math.round((score / total) * 100) : 0;
        const passed = percent >= PASS_THRESHOLD;
      
        return (
          <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
            <h2 style={{ color: "#d4145a" }}>Quiz Complete!</h2>
            <p style={{ fontSize: 22, margin: "18px 0" }}>
              Your Score: {score}/{total} ({total > 0 ? percent : "N/A"}%)
            </p>
            {passed ? (
              <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
                🎉 Great job—you passed this chapter!
              </div>
            ) : (
              <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
                Score at least {PASS_THRESHOLD}% to unlock the next chapter.
              </div>
            )}
            {passed ? (
              <button
                className="button1"
                style={{ marginTop: 28 }}
                onClick={() => {
                  setQuizResult(null);
                  setCurrent(i => i + 1); // Go to the next chapter!
                }}
              >
                Next Chapter
              </button>
            ) : (
              <button
                className="button1"
                style={{ marginTop: 28 }}
                onClick={() => setQuizResult(null)}
              >
                Return to Lesson
              </button>
            )}
          </div>
        );
      }      
  
    /* ─────────── render: normal lesson page ─────────── */
    return (
      <div className="lesson-page">
        <ProgressBar
          progress={Math.round((current / (Grade11BiologyLessonPages.length - 1)) * 100)}
        />
  
        <LessonUI
          pages={[Grade11BiologyLessonPages[current]]}
          currentPageIndex={0}
          onPrevPage={current > 0 ? () => setCurrent(i => i - 1) : undefined}
          onNextPage={current < Grade11BiologyLessonPages.length - 1 ? handleNextPage : undefined}
          backTo="/science"
        />
  
        {/* manual quiz launch (optional practice / retake) */}
        {currentTopic !== "Course Introduction" && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button
              className="button1"
              onClick={() => {
                setShowQuiz(true);
                setQuizResult(null);
              }}
            >
              Start / Retake Quiz for This Chapter
            </button>
          </div>
        )}
      </div>
    );
  }
